import { isAxiosError } from 'axios';
import i18n from 'i18next';

export type FieldError = { field: string; message: string };

export type Problem = {
  status: number;
  title: string;
  detail?: string;
  code?: string;
  errors?: FieldError[];
  /** Extra fields of the problem (e.g. limit, max, feature) used to fill the translated message. */
  details?: Record<string, unknown>;
};

type ProblemBody = Partial<Problem> & { message?: string };

const STANDARD_FIELDS = new Set(['status', 'title', 'detail', 'code', 'errors', 'message', 'type', 'instance', 'correlationId']);

/** Normalises any thrown value into an RFC 9457-shaped Problem the UI can render. */
export function toProblem(err: unknown): Problem {
  if (isAxiosError(err)) {
    if (!err.response) {
      return { status: 0, title: i18n.t('common.networkError'), detail: i18n.t('common.networkDetail') };
    }
    const body = (err.response.data ?? {}) as ProblemBody;
    const problem: Problem = {
      status: body.status ?? err.response.status,
      title: body.title ?? err.response.statusText ?? i18n.t('common.requestFailed')
    };
    const detail = body.detail ?? body.message;
    if (detail) problem.detail = detail;
    if (body.code) problem.code = body.code;
    if (body.errors) problem.errors = body.errors;
    const rest = Object.fromEntries(Object.entries(body as Record<string, unknown>).filter(([k]) => !STANDARD_FIELDS.has(k)));
    if (Object.keys(rest).length > 0) problem.details = rest;
    return problem;
  }
  const detail = err instanceof Error ? err.message : String(err);
  return { status: 500, title: i18n.t('common.unexpected'), detail };
}

/**
 * Human message for a problem in the active language: the translation of its stable `code` when
 * known, else the server `detail`, else the provided fallback, else the title.
 */
export function problemMessage(problem: Problem, fallback?: string): string {
  if (problem.code) {
    const key = `errors.${problem.code}`;
    if (i18n.exists(key)) {
      const d = problem.details ?? {};
      const values: Record<string, unknown> = { ...d };
      if (typeof d['limit'] === 'string') values['limitName'] = i18n.t(`billing.limits.${d['limit']}` as never, { defaultValue: d['limit'] });
      if (typeof d['feature'] === 'string') values['featureName'] = i18n.t(`billing.gate.features.${d['feature']}` as never, { defaultValue: d['feature'] });
      return String(i18n.t(key as never, values as never));
    }
  }
  return problem.detail ?? fallback ?? problem.title;
}

/** Shorthand for `problemMessage(toProblem(err), fallback)`. */
export function errorMessage(err: unknown, fallback?: string): string {
  return problemMessage(toProblem(err), fallback);
}
